import { useAuthState } from 'auth/hooks/useAuth';
import customStore from 'caches/utils/localStorage';
import { EXPERIMENT_STORE } from 'experiments/utils/constants';
import { isExperimentActive, logExperimentExposure } from 'experiments/utils';
import { FeatureFlags } from 'featureFlags/constants';
import { useCallback, useEffect, useState } from 'react';
import { useAbortController, useFeatureFlag } from 'shared/hooks';
import { getMParticleDeviceId } from 'tracking/utils/getMparticleDeviceId';
import { getMParticleSessionId } from 'tracking/utils/getMParticleSessionId';
import { bugsnag } from 'shared/utils/services/bugsnag';

const attributePrefix = 'data-experiment';

// Reference:
// https://stackoverflow.com/questions/71687472/trigger-a-custom-event-when-element-in-viewport-javascript
// https://jsdoc.app/tags-param.html

/**
 * @name isElementInViewport
 * @description Determine whether or not a node is visible in viewport
 * @param {Object} el the node
 *
 * @returns {boolean}
 */

const isElementInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  // an element is in viewport if
  // top & left position are positive
  // and if bottom and right positions are less than the height & width of the window respectively
  const isIn =
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window?.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window?.innerWidth || document.documentElement.clientWidth);

  const experimentName = el.getAttribute(attributePrefix);
  const loggedExperiments = customStore?.getItem(EXPERIMENT_STORE.LOGGED_EXPERIMENTS) || {};
  const activeExperiments = customStore?.getItem(EXPERIMENT_STORE.ACTIVE_EXPERIMENTS) || {};

  // only fire event if the element is attached to an active experiments
  // and has not been logged already

  return isIn && !loggedExperiments[experimentName] && activeExperiments[experimentName];
};

const _observerCallback = (entries) => {
  const allNodesInViewPort = entries.filter((event) => {
    return isElementInViewport(event.target);
  });
  for (const entry of allNodesInViewPort) {
    // list is non-empty which means
    // some [data-experiment] elements entered the viewport
    const inViewportCustomEvent = new CustomEvent('inViewport', {
      detail: {
        target: entry,
        /* put the data to be carried here */
      },
    });
    // dispatch the event
    document.dispatchEvent(inViewportCustomEvent);
  }
};

// threshhold 1 means the element is 100% visible
// from top to bottom
function _createObserver() {
  const options = {
    root: document,
    threshold: 1,
  };

  return new IntersectionObserver(_observerCallback, options);
}

interface IEvent extends CustomEvent {
  detail: {
    target?: IntersectionObserverEntry;
  };
}

function _setupListener(logExposure) {
  document.addEventListener(
    'inViewport',
    (e: IEvent) => {
      const triggeredElement = e.detail.target;
      // this check mitigates flashing content a bit
      // if intersectionRect perimeter is 0 then the components can be seen on screen
      if (
        triggeredElement.intersectionRect.width > 0 &&
        triggeredElement.intersectionRect.height > 0
      ) {
        logExposure(triggeredElement);
      }
    },
    false,
  );
}

export function startTracking(logExposure) {
  // List of all containers elements
  // we may potentially run experiments in
  // eg: can add 'site-header' to the list
  const containerElements = ['#main-page-layout', '#site-footer', '#site-header'];

  // add listerner(s) to the document
  // here we can add multiple listeners at the time
  // eg: click, hover etc if necessary
  _setupListener(logExposure);
  const inViewPortObserver = _createObserver();

  for (const containerElement of containerElements) {
    // get the node component from the selector string
    const containerComponent = document.querySelector(containerElement);
    if (containerComponent) {
      // for each '[data-experiment]' nodes in the above parent container, observe inviewport event
      for (const experimentTrigger of containerComponent.querySelectorAll(`[${attributePrefix}]`)) {
        // add more observers or listeners here
        inViewPortObserver.observe(experimentTrigger);
      }
    }
  }
}

export function TrackExperimentsScript() {
  const allowExperiments = useFeatureFlag(FeatureFlags.WEB_ALLOW_EXPERIMENTS);
  const activeExperiments = customStore.getItem(EXPERIMENT_STORE.ASSIGNED_EXPERIMENTS);
  // this is used in a useEffect to prevent starting tracking
  // multiple times
  const hasAssignedExperiments = (activeExperiments || []).length > 0;
  const { isAuthenticated, user } = useAuthState();
  const sessionId = getMParticleSessionId();
  const deviceId = getMParticleDeviceId();
  const { signal } = useAbortController()();
  const [loggingExperiments, setLoggingExperiments] = useState({});

  const logDarwinExperiment = useCallback(
    (experimentName) => {
      const loggedExperiments = customStore.getItem(EXPERIMENT_STORE.LOGGED_EXPERIMENTS) || {};
      // Can log exposure if it's not already logging from a previous promise
      // if it's not already logged
      // if it's an active & unexpired experiment
      const canLogExposure =
        !loggingExperiments[experimentName] &&
        !loggedExperiments?.[experimentName] &&
        activeExperiments?.[experimentName] &&
        isExperimentActive(activeExperiments?.[experimentName]?.endTimestamp);

      if (canLogExposure) {
        // since we are not doing async calls we ought to keep track of
        // all experiments currently logging so that if the element
        // appears in the viewport multiple times we are logging them only once
        setLoggingExperiments({ ...loggingExperiments, [experimentName]: true });
        void logExperimentExposure({
          deviceId,
          experimentName,
          isAuthenticated,
          optionalMetaData: {
            sessionId,
            userId: user?.id,
          },
          signal,
        })
          .then(() => {
            setLoggingExperiments({ ...loggingExperiments, [experimentName]: undefined });
          })
          .catch((error) => {
            bugsnag(error, { context: 'Logging experiment exposure' });
          });
      }
    },
    [loggingExperiments, activeExperiments, deviceId, isAuthenticated, sessionId, user?.id, signal],
  );

  useEffect(() => {
    function logExposure(experimentTarget) {
      const experimentName = experimentTarget?.target?.dataset?.experiment;

      if (experimentName) {
        void logDarwinExperiment(experimentName);
      }
    }
    // only start tracking if current deviceId has been assigned
    if (allowExperiments && hasAssignedExperiments && deviceId) {
      startTracking(logExposure);
    }
  }, [allowExperiments, deviceId, hasAssignedExperiments, logDarwinExperiment]);

  return null;
}
